/**
 * ChangePasswordModal — modal cancelable para cambiar la contraseña del
 * usuario autenticado, con dos rutas:
 *
 *   1) "Tengo mi contraseña actual": pide la actual, valida con
 *      signInWithPassword sobre un cliente auxiliar (NO toca la sesión
 *      aal2 vigente del usuario), y si pasa, hace auth.updateUser.
 *
 *   2) "Olvidé mi contraseña": pide el código TOTP de la app
 *      autenticadora y, si verifica, permite cambiar la contraseña.
 *      Aprovecha que el usuario ya está logueado (aal2) — el TOTP fresco
 *      sustituye la prueba de "knowledge factor" que ya no recuerda.
 *
 * Reglas de contraseña nueva: mínimo 12 caracteres, al menos una
 * mayúscula, una minúscula, un dígito y un símbolo. Se admiten
 * espacios en blanco (incluyendo intermedios) como caracteres válidos.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { createClient } from '@supabase/supabase-js';
import { getSupabase } from '../client';

type Mode = 'current' | 'totp';

interface ChangePasswordModalProps {
  /** Email del usuario autenticado, necesario para la re-validación con contraseña actual. */
  email: string;
  /** Cierra el modal sin cambios (botón cancelar / overlay / Esc). */
  onClose: () => void;
  /** Llamado tras un cambio de contraseña exitoso. */
  onSuccess?: () => void;
}

const MIN_LEN = 12;
const SYMBOL_RE = /[^A-Za-z0-9\s]/;

function validateNewPassword(pwd: string): string | null {
  if (pwd.length < MIN_LEN) return `La nueva contraseña debe tener al menos ${MIN_LEN} caracteres.`;
  if (!/[a-z]/.test(pwd)) return 'Debe contener al menos una letra minúscula.';
  if (!/[A-Z]/.test(pwd)) return 'Debe contener al menos una letra mayúscula.';
  if (!/[0-9]/.test(pwd)) return 'Debe contener al menos un dígito.';
  if (!SYMBOL_RE.test(pwd)) return 'Debe contener al menos un símbolo (ej. !@#$%&*).';
  return null;
}

export function ChangePasswordModal({ email, onClose, onSuccess }: ChangePasswordModalProps): ReactNode {
  const [mode, setMode] = useState<Mode>('current');
  const [current, setCurrent] = useState('');
  const [totp, setTotp] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Limpia el campo no-aplicable al cambiar de modo (evita arrastrar PII residual).
  useEffect(() => {
    setError(null);
    if (mode === 'current') setTotp('');
    else setCurrent('');
  }, [mode]);

  function validate(): string | null {
    if (mode === 'current') {
      if (!current) return 'Introduce tu contraseña actual.';
    } else {
      if (!/^\d{6}$/.test(totp)) return 'El código TOTP son 6 dígitos numéricos.';
    }
    if (mode === 'current' && next === current) {
      return 'La nueva contraseña debe ser distinta de la actual.';
    }
    if (next !== confirm) return 'Las contraseñas nuevas no coinciden.';
    const pwdErr = validateNewPassword(next);
    if (pwdErr) return pwdErr;
    return null;
  }

  async function reauthWithCurrentPassword(): Promise<void> {
    const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined;
    const anon = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string | undefined;
    if (!url || !anon) throw new Error('Supabase no está configurado en este entorno.');
    const probe = createClient(url, anon, {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
    });
    const { error: signinErr } = await probe.auth.signInWithPassword({ email, password: current });
    await probe.auth.signOut().catch(() => undefined);
    if (signinErr) throw new Error('La contraseña actual es incorrecta.');
  }

  async function reauthWithTotp(): Promise<void> {
    const sb = getSupabase();
    const { data: factorsData, error: lfErr } = await sb.auth.mfa.listFactors();
    if (lfErr) throw lfErr;
    const verified = factorsData?.totp?.find((f) => f.status === 'verified');
    if (!verified) throw new Error('No tienes una app autenticadora configurada en esta cuenta.');
    const { data: challenge, error: chErr } = await sb.auth.mfa.challenge({ factorId: verified.id });
    if (chErr || !challenge) throw chErr ?? new Error('No se pudo iniciar el desafío TOTP.');
    const { error: vErr } = await sb.auth.mfa.verify({
      factorId: verified.id,
      challengeId: challenge.id,
      code: totp,
    });
    if (vErr) throw new Error('Código TOTP incorrecto o expirado.');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErr = validate();
    if (validationErr) {
      setError(validationErr);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'current') await reauthWithCurrentPassword();
      else await reauthWithTotp();
      const sb = getSupabase();
      const { error: updErr } = await sb.auth.updateUser({ password: next });
      if (updErr) throw updErr;
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget && !submitting) onClose();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
      style={overlayStyle}
      onClick={handleOverlayClick}
    >
      <form
        onSubmit={handleSubmit}
        style={cardStyle}
        onKeyDown={(e) => { if (e.key === 'Escape' && !submitting) onClose(); }}
      >
        <h2 id="change-password-title" style={{ margin: 0, fontSize: 20 }}>
          {success ? 'Contraseña actualizada' : 'Cambiar contraseña'}
        </h2>

        {success ? (
          <>
            <p style={helpStyle}>
              Tu nueva contraseña ya está activa. La próxima vez que inicies sesión deberás usarla
              (seguirás necesitando tu app autenticadora como segundo factor).
            </p>
            <button type="button" onClick={onClose} style={primaryBtnStyle}>
              Entendido
            </button>
          </>
        ) : (
          <>
            <div role="tablist" aria-label="Método de verificación" style={tabsStyle}>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'current'}
                onClick={() => setMode('current')}
                style={mode === 'current' ? tabActiveStyle : tabStyle}
                disabled={submitting}
              >
                Con mi contraseña actual
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'totp'}
                onClick={() => setMode('totp')}
                style={mode === 'totp' ? tabActiveStyle : tabStyle}
                disabled={submitting}
              >
                La olvidé · Usar TOTP
              </button>
            </div>

            {mode === 'current' ? (
              <>
                <p style={helpStyle}>
                  Por seguridad, confirma tu contraseña actual antes de elegir una nueva.
                </p>
                <label style={labelStyle}>
                  <span>Contraseña actual</span>
                  <input
                    type="password"
                    value={current}
                    onChange={(e) => { setCurrent(e.target.value); setError(null); }}
                    autoComplete="current-password"
                    required
                    autoFocus
                    style={inputStyle}
                  />
                </label>
              </>
            ) : (
              <>
                <p style={helpStyle}>
                  Abre tu app autenticadora (Google Authenticator, Authy, 1Password…)
                  y escribe el código de 6 dígitos vigente.
                </p>
                <label style={labelStyle}>
                  <span>Código TOTP (6 dígitos)</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={totp}
                    onChange={(e) => {
                      // Solo dígitos. Bloquea pegado de "123 456" con espacio.
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setTotp(v); setError(null);
                    }}
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                    placeholder="123456"
                  />
                </label>
              </>
            )}

            <label style={labelStyle}>
              <span>Nueva contraseña</span>
              <input
                type="password"
                value={next}
                onChange={(e) => { setNext(e.target.value); setError(null); }}
                autoComplete="new-password"
                minLength={MIN_LEN}
                required
                style={inputStyle}
                placeholder={`mín. ${MIN_LEN} carac., mayús + minús + dígito + símbolo`}
              />
            </label>
            <label style={labelStyle}>
              <span>Confirma la nueva contraseña</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(null); }}
                autoComplete="new-password"
                required
                style={inputStyle}
              />
            </label>
            <p style={{ ...helpStyle, fontSize: 11, marginTop: 0 }}>
              Se admiten espacios y símbolos (! @ # $ % & * - _ … ).
            </p>

            {error && (
              <div role="alert" style={{ color: '#e57373', fontSize: 13, marginTop: 4 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={secondaryBtnStyle}
              >
                Cancelar
              </button>
              <button type="submit" disabled={submitting} style={{ ...primaryBtnStyle, marginTop: 0, flex: 1 }}>
                {submitting ? 'Guardando…' : 'Guardar nueva contraseña'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
  display: 'grid', placeItems: 'center', zIndex: 9999,
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  background: 'var(--bg-deep, #0c1216)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 12,
  padding: 24,
  width: 'min(460px, 100%)',
  display: 'flex', flexDirection: 'column', gap: 12,
  fontFamily: 'Inter, system-ui, sans-serif',
  color: 'var(--text, rgba(255,255,255,0.92))',
};
const labelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-dim)',
};
const inputStyle: React.CSSProperties = {
  height: 38, padding: '0 12px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.04)', color: 'var(--text)',
  fontSize: 13,
};
const primaryBtnStyle: React.CSSProperties = {
  marginTop: 8, padding: '10px 16px',
  background: 'var(--btn-primary-bg, #b4c96f)', color: '#0c1216',
  border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
  fontSize: 14,
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '10px 16px',
  background: 'transparent', color: 'var(--text, rgba(255,255,255,0.85))',
  border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6,
  fontWeight: 500, cursor: 'pointer', fontSize: 14,
};
const helpStyle: React.CSSProperties = {
  color: 'var(--text-dim, rgba(255,255,255,0.7))',
  fontSize: 13, marginTop: 4, marginBottom: 0,
};
const tabsStyle: React.CSSProperties = {
  display: 'flex', gap: 4, padding: 4,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 8,
};
const tabBase: React.CSSProperties = {
  flex: 1, padding: '7px 10px',
  fontSize: 12, fontWeight: 500,
  border: '1px solid transparent', borderRadius: 6,
  background: 'transparent', color: 'var(--text-dim, rgba(255,255,255,0.65))',
  cursor: 'pointer',
};
const tabStyle: React.CSSProperties = { ...tabBase };
const tabActiveStyle: React.CSSProperties = {
  ...tabBase,
  background: 'rgba(180, 201, 111, 0.14)',
  borderColor: 'rgba(180, 201, 111, 0.42)',
  color: 'var(--text, rgba(255,255,255,0.95))',
};
